import React, { ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { RulesEditorProviderValue, RulesEditorContext } from ".";
import { Section } from "interfaces/Phoenix.FormsDesigner.Models";
import { classes } from "components/Utility";
import IconButton from "components/Shared/IconButton";
import { Button, MSelect } from "components/Shared";
import ReorderableGrid, { ReorderableItem } from "components/Shared/ReorderableGrid";
import { stopPropagationPreventDefaultNoFn } from "tools/clickEvents";
import RulesEditorFormControl, { reorderQuestionHandle } from "./RulesEditorFormControl";
import { TextField } from "@mui/material";
import { MSelectOption } from "components/Shared/MSelect";

const reorderSectionHandle = "reorder-section-handle";

interface RulesSectionProps {
  section: Section;
  sectionIndex: number;
  isCollapsed: boolean;
  setCollapsed: (newValue: boolean) => void;
  setAllCollapsed: (newValue: boolean) => void;
  areAllCollapsed: boolean;
}

const RulesSection = ({ section, isCollapsed, setCollapsed, setAllCollapsed, areAllCollapsed }: RulesSectionProps) => {
  let context = useContext<RulesEditorProviderValue>(RulesEditorContext);
  const sectionIndex = context.rulesetDefinition.pages[0].sections.indexOf(section);
  const [newSectionTitle, setNewSectionTitle] = useState<string>();
  const [isRenamingSection, setIsRenamingSection] = useState<boolean>();

  const controls = useMemo<ReorderableItem[]>(
    () => section.mobileQuestions.map((qid) => {
      return {
        id: qid,
        node: <RulesEditorFormControl
                key={qid}
                question={context.getQuestionById(qid)}
                parentSection={section}
               />,
      };
    }),
    [context.rulesetDefinitionUpdatedAt]);

  const editSectionName = () => {
    setIsRenamingSection(true);
    setNewSectionTitle(section.title);
  };

  const cancelEditingSectionName = () => {
    setIsRenamingSection(false);
  };

  const saveSectionName = () => {
    context.editFns.renameSection(sectionIndex, newSectionTitle);
    cancelEditingSectionName();
  };

  let titleNode = (
    <>
      <IconButton icon="edit" onClick={editSectionName}/>
      <span>{section.title}</span>
    </>
  );

  if (isRenamingSection) {
    titleNode = (
      <>
        <IconButton icon="clear" onClick={cancelEditingSectionName}/>
        <IconButton icon="check" onClick={saveSectionName}/>
        <span><TextField value={newSectionTitle} onChange={(e) => setNewSectionTitle(e.target.value)} variant="standard" /></span>
      </>
    );
  }

  return (
    <div className="form-section">
      <div className="section-title">
        {areAllCollapsed && <IconButton icon="reorder" className={reorderSectionHandle} onClick={stopPropagationPreventDefaultNoFn}/>}
        <IconButton icon="keyboard_arrow_down" className={classes("md-collapser", !isCollapsed && "md-collapser--flipped")} onClick={() => setCollapsed(!isCollapsed)}/>
        <IconButton icon="keyboard_double_arrow_down" className={classes("md-collapser", !isCollapsed && "md-collapser--flipped")} onClick={() => setAllCollapsed(!isCollapsed)}/>
        {titleNode}
        <IconButton icon="delete" onClick={() => context.editFns.deleteSection(sectionIndex)}/>
      </div>
      {!isCollapsed && <>
        <ReorderableGrid
          className="section-controls"
          items={controls}
          handleSelector={`.${reorderQuestionHandle}`}
          onReorder={ids => context.editFns.reorderQuestions({ section }, ids)}
        />
        <div className="new-question-container">
          <Button
            primary
            onClick={() => context.editFns.addNewQuestion({ section })}
          >
            Add New Question
          </Button>
          <Button
            primary
            onClick={() => context.editFns.addNewSection(sectionIndex)}
          >
            Add New Section
          </Button>
        </div>
        </>}
      </div>
    );
};

const RulesEditorPreview = () => {
  let context = useContext<RulesEditorProviderValue>(RulesEditorContext);

  const sections = context.rulesetDefinition.pages[0].sections;

  const [sectionCollapseStatus, _setSectionCollapseStatus] = useState<boolean[]>(() => sections.map(_ => false));
  const [editingFormIndex, setEditingFormIndex] = useState<number>();
  const [editingFormValue, setEditingFormValue] = useState<MSelectOption<string>>();

  useEffect(() => {
    if (context.lastChangeType === "add section" || context.lastChangeType === "delete section") {
      let newSections = [ ...sectionCollapseStatus ];
      if (context.lastChangeType === "add section") newSections.splice(context.lastChangeData as number + 1, 0, false);
      if (context.lastChangeType === "delete section") newSections.splice(context.lastChangeData as number, 1);
      _setSectionCollapseStatus(newSections);
    }
  }, [context.rulesetDefinitionUpdatedAt]);

  const setCollapseStatus = (sectionIndex: number, newValue: boolean) => {
    let newStatus = [ ...sectionCollapseStatus ];
    newStatus[sectionIndex] = newValue;
    _setSectionCollapseStatus(newStatus);
  };

  const setAllCollapseStatus = (newValue: boolean) => {
    let newStatus = [ ...sectionCollapseStatus ];
    sections.forEach((_, i) => newStatus[i] = newValue);
    _setSectionCollapseStatus(newStatus);
  };

  const areAllCollapsed = !sectionCollapseStatus.some(s => !s);

  const changeLinkedForm = () => {
    context.editFns.changeLinkedForm(editingFormIndex, editingFormValue?.value);
    cancelEditingLinkedForm();
  };

  const cancelEditingLinkedForm = () => {
    setEditingFormIndex(null);
    setEditingFormValue(null);
  };

  const formOptions = context.formsList?.filter(f => !!f.phoenixFormId).map(f => ({ label: f.name, value: f.phoenixFormId }));

  const startEditingForm = (index: number) => {
    setEditingFormIndex(index);
    setEditingFormValue(formOptions.find(o => o.value === sections[index].linkedFormDefinitionId));
  };

  const sectionItems = useMemo<ReorderableItem[]>(
    () => sections.map((s, i) => {
      let linkedForm: ReactNode;

      if (editingFormIndex === i && context.formsList) {
        linkedForm = (
          <>
            <MSelect
              value={editingFormValue}
              onChange={v => {setEditingFormValue(v);console.log("editing val changed ", v, s);}}
              options={formOptions}
            />
            <IconButton icon="check" onClick={changeLinkedForm} />
            <IconButton icon="clear" onClick={cancelEditingLinkedForm} />
          </>);
      } else {
        linkedForm = (
          <>{s.linkedFormDefinitionId ?
            <>Linked Form: <b>{s.linkedFormDefinitionName}</b></> :
            "No Form Linked"}
            <IconButton icon="assignment_add" onClick={() => startEditingForm(i)} />
          </>);
      }

      return {
        id: `${i}`,
        node: (
          <>
            <div className="section-linked-form">
              {linkedForm}
            </div>
            <RulesSection
              key={s.mobileQuestions[0] ?? s.title}
              section={s}
              sectionIndex={i}
              isCollapsed={sectionCollapseStatus[i]}
              setCollapsed={val => setCollapseStatus(i, val)}
              setAllCollapsed={setAllCollapseStatus}
              areAllCollapsed={areAllCollapsed}
            />
          </>
        ),
      }
    }),
    [context.rulesetDefinitionUpdatedAt, sectionCollapseStatus, editingFormIndex, formOptions]);


  return (
    <div className="phoenixform-designer-preview rules-editor">
      <ReorderableGrid
        className="reorderable-sections"
        items={sectionItems}
        handleSelector={`.${reorderSectionHandle}`}
        onReorder={ids => context.editFns.reorderSections(ids.map(i => Number.parseInt(i)))}
      />
      {(!sectionCollapseStatus.length || sectionCollapseStatus[sections.length - 1]) && (
        <div className="bottom-new-section-button">
          <Button
            primary
            onClick={() => context.editFns.addNewSection(sections.length - 1)}
          >
            Add New Section
          </Button>
        </div>
      )}
    </div>
  );
};

export default RulesEditorPreview;
